import React, { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Hook para manejar columnas redimensionables
 * @param {Array} initialColumns - Array de columnas con {key, header, width, minWidth}
 * @param {string} storageKey - Key para guardar anchos en localStorage
 */
export const useResizableColumns = (initialColumns, storageKey = null) => {
  const [columnWidths, setColumnWidths] = useState(() => {
    // Intentar cargar anchos guardados
    if (storageKey) {
      try {
        const saved = localStorage.getItem(`table-widths-${storageKey}`);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {}
    }
    // Usar anchos iniciales
    return initialColumns.reduce((acc, col) => {
      acc[col.key] = col.width || 100;
      return acc;
    }, {});
  });

  const saveWidths = useCallback((widths) => {
    if (storageKey) {
      try {
        localStorage.setItem(`table-widths-${storageKey}`, JSON.stringify(widths));
      } catch (e) {}
    }
  }, [storageKey]);

  const handleResize = useCallback((columnKey, newWidth) => {
    const column = initialColumns.find(c => c.key === columnKey);
    const minWidth = column?.minWidth || 50;
    const finalWidth = Math.max(minWidth, newWidth);
    
    setColumnWidths(prev => {
      const updated = { ...prev, [columnKey]: finalWidth };
      saveWidths(updated);
      return updated;
    });
  }, [initialColumns, saveWidths]);

  const resetWidths = useCallback(() => {
    const defaults = initialColumns.reduce((acc, col) => {
      acc[col.key] = col.width || 100;
      return acc;
    }, {});
    setColumnWidths(defaults);
    saveWidths(defaults);
  }, [initialColumns, saveWidths]);

  return { columnWidths, handleResize, resetWidths };
};

/**
 * Componente de cabecera de columna redimensionable
 */
export const ResizableHeader = ({ children, width, onResize, minWidth = 50, className = '' }) => {
  const headerRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  }, [width]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - startXRef.current;
      const newWidth = Math.max(minWidth, startWidthRef.current + diff);
      onResize(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, minWidth, onResize]);

  return (
    <th
      ref={headerRef}
      className={`relative select-none ${className}`}
      style={{ width: `${width}px`, minWidth: `${minWidth}px` }}
    >
      {children}
      {/* Resize handle */}
      <div
        onMouseDown={handleMouseDown}
        className={`absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : 'bg-transparent hover:bg-blue-300'}`}
        style={{ touchAction: 'none' }}
      />
    </th>
  );
};

/**
 * Celda de tabla con ancho controlado
 */
export const ResizableCell = ({ children, width, className = '' }) => {
  return (
    <td
      className={className}
      style={{ width: `${width}px`, maxWidth: `${width}px` }}
    >
      <div className="truncate" style={{ maxWidth: `${width - 24}px` }}>
        {children}
      </div>
    </td>
  );
};

export default { useResizableColumns, ResizableHeader, ResizableCell };
